import { ContactConflictAction } from "@/modules/lead-page/components/contact-conflicts/action";
import { useState } from "react";
import { useLeadContext } from "@/modules/lead-page/context/lead-page.context";
import MergeLeads from "@/modules/lead-page/components/lead-info/lead-actions/merge-lead/merge-lead";
import CopyZoomLink from "@/modules/lead-page/components/lead-info/lead-actions/copy-zoom-link";
import AutoAssign from "@/modules/lead-page/components/lead-info/lead-actions/auto-assign";
import ArchiveLead from "@/modules/lead-page/components/lead-info/lead-actions/archive-lead";
import DeleteLead from "@/modules/lead-page/components/lead-info/lead-actions/delete-lead";
import DoNotCall from "@/modules/lead-page/components/lead-info/lead-actions/do-not-call";
import ShareLeadWhatsapp from "@/modules/lead-page/components/lead-info/lead-actions/share-lead-whatsapp";
import { MoreHorizontal } from "lucide-react";
import useReferToEntity from "@/modules/lead-page/hooks/lead-info/lead-actions/refer-to-entity.hook";
import FeedBackModal from "@/modules/common/components/modals/feedback-modal";
import ConfirmationModal from "@/modules/common/components/modals/cofirmation-modal";
import FeatureRouter from "@/modules/common/components/layout/side-menu/old-navbar/feature-router";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import usePassport from "@/modules/common/hooks/passport";
import { userHasPermission } from "@/modules/common/utils";
import ManageReferrals from "@/modules/lead-page/components/lead-info/manage-referrals/manage-referrals";
import MarkAsRubbish from "@/modules/lead-page/components/lead-info/lead-actions/mark-as-rubbish";
import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import { AddTagsAction } from "@/modules/lead-page/new/components/header/actions/add-tags-action";
import { ChangeLeadAction } from "@/modules/lead-page/new/components/header/actions/change-lead-action";
import { GenerateQrCode } from "@/modules/lead-page/new/components/header/actions/generate-qr-code/generate-qr-code";
import { CopyAccessLink } from "@/modules/lead-page/new/components/header/actions/copy-access-link";
import { ActionsDropdown } from "@/modules/lead-page/new/components/header/actions/actions-drop-down";
import { IconButtonVariant } from "@/modules/common/components/ui-kit/buttons/shared/types";

export const LeadActions = ({
  iconVariant = "outlined-secondary",
}: {
  iconVariant?: IconButtonVariant;
}) => {
  const { lead } = useLeadContext();
  const { permissions: currentUserPermissions } = usePassport();

  const hideLeadTypeActions = userHasPermission(
    {
      name: PermissionsEnum.HIDE_LEAD_TYPE_ACTIONS,
      level: PermissionLevel.READ,
    },
    currentUserPermissions,
  );

  const [mergeLeadModalOpen, setMergeLeadModalOpen] = useState(false);
  const {
    isConfirmTransferOpen,
    entityName,
    entityKey,
    isFeedBackModalOpen,
    setIsFeedBackModalOpen,
    referToEntity,
    handleIsConfirmModalOpen,
    isLoading,
    isError,
  } = useReferToEntity();

  const leadActions = [
    { name: "Resolve contact conflict", jsx: <ContactConflictAction /> },
    {
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.MERGE_JOURNEY,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <MergeLeads
            mergeLeadModalOpen={mergeLeadModalOpen}
            setMergeLeadModalOpen={setMergeLeadModalOpen}
          />
        </PermissionGuard>
      ),
      name: "Merge Lead",
    },
    {
      name: "Auto Assign",
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.AUTO_ASSIGN_JOURNEY,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <AutoAssign />
        </PermissionGuard>
      ),
    },
    {
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.MANAGE_REFERRALS,
              level: PermissionLevel.ALL,
            },
          ]}
        >
          <ManageReferrals />
        </PermissionGuard>
      ),
      name: "Manage Referrals",
    },
    {
      name: "Get Zoom Link",
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.GET_ZOOM_LINK,
              level: PermissionLevel.READ,
            },
            {
              name: PermissionsEnum.ASSIGNED_AGENT,
              level: PermissionLevel.ALL,
            },
          ]}
        >
          <CopyZoomLink />
        </PermissionGuard>
      ),
    },
    {
      name: "Share Via Whatsapp",
      jsx: <ShareLeadWhatsapp />,
    },
    {
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.LEAD_TAGS,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <AddTagsAction />
        </PermissionGuard>
      ),
      name: "Add Tags",
    },
    ...(!hideLeadTypeActions
      ? [
          {
            jsx: <ChangeLeadAction />,
            name: "Lead Type Actions",
          },
        ]
      : []),
    {
      name: "Generate QR Code",
      jsx: <GenerateQrCode />,
    },
    {
      name: "Copy Access Link",
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.JOURNEY_COPY_ACCESS_LINK,
              level: PermissionLevel.READ,
            },
          ]}
        >
          <CopyAccessLink />
        </PermissionGuard>
      ),
    },
    {
      name: "Do Not Call",
      jsx: <DoNotCall />,
    },
    {
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.ARCHIVE_JOURNEY,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <ArchiveLead />
        </PermissionGuard>
      ),
      name: "Archive Lead",
    },
    {
      jsx: (
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.MARK_AS_RUBBISH,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <MarkAsRubbish />
        </PermissionGuard>
      ),
      name: "Mark as Rubbish",
    },
    {
      jsx: (
        <FeatureRouter
          featureKey={"lead-delete-button"}
          element={
            <PermissionGuard
              permissions={[
                {
                  name: PermissionsEnum.DELETE_JOURNEY,
                  level: PermissionLevel.WRITE,
                },
              ]}
            >
              <DeleteLead />
            </PermissionGuard>
          }
        />
      ),
      name: "Delete Lead",
    },
  ];

  const triggerButton = (
    handleClick: (event: React.MouseEvent<HTMLElement>) => void,
  ) => (
    <CommonIconButton variant={iconVariant} size="md" onClick={handleClick}>
      <MoreHorizontal size={20} />
    </CommonIconButton>
  );

  return (
    <>
      <ActionsDropdown
        triggerButton={triggerButton}
        items={leadActions}
        shouldRenderItem={(item) =>
          !(item.name === "Delete Lead" && !lead?.data?.lead?.archived) &&
          (item.name !== "Generate QR Code" ||
            !!lead?.data?.lead?.location?.isExpo) &&
          (item.name !== "Copy Access Link" ||
            !!lead?.data?.lead?.location?.isExpo)
        }
      />

      <ConfirmationModal
        open={isConfirmTransferOpen}
        setOpen={() => handleIsConfirmModalOpen("", "")}
        title="Transfer Lead"
        body={`Are you sure you want to transfer this lead to ${entityName}?`}
        onConfirmHandler={() => referToEntity(entityKey)}
        isLoading={isLoading}
      />
      <FeedBackModal
        open={isFeedBackModalOpen}
        setOpen={setIsFeedBackModalOpen}
        failure={isError}
        title={
          isError
            ? "Something went wrong"
            : "Lead has been transferred successfully"
        }
        message={isError ? "Please try again later:" : ""}
        handleOnClickOk={() => setIsFeedBackModalOpen(false)}
      />
    </>
  );
};
